<script setup>
import { computed, onMounted, ref } from 'vue';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { authorizeTray } from '../../../api/tray';

defineProps({
  error: { type: String, default: '' },
});

const store = useStore();
const { t } = useI18n();
const integrationLoaded = ref(false);
const isSubmitting = ref(false);
const viewMode = ref('overview');
const editingHookId = ref(null);
const storeUrl = ref('');
const consumerKey = ref('');
const consumerSecret = ref('');
const formError = ref('');

const integration = useFunctionGetter('integrations/getIntegration', 'tray');
const integrationId = computed(() => integration.value?.id || 'tray');
const integrationName = computed(() => integration.value?.name || 'Tray');
const connectedHooks = computed(() => {
  return (integration.value?.hooks || []).map(hook => {
    const settings = hook.settings || {};

    return {
      id: hook.id,
      storeUrl: settings.store_url || hook.reference_id || '',
      storeHost: settings.store_host || '',
      storeName: settings.store_name || '',
      storeId: settings.store_id || '',
      apiAddress: settings.api_address || '',
      webhookToken: settings.webhook_token || '',
    };
  });
});
const integrationDescription = computed(
  () =>
    integration.value?.description || t('INTEGRATION_SETTINGS.TRAY.DESCRIPTION')
);
const selectedHook = computed(
  () =>
    connectedHooks.value.find(hook => hook.id === editingHookId.value) || null
);
const accountId = computed(() => store.getters.getCurrentAccountId);
const pluginConfig = computed(
  () => window.chatwoot?.plugins?.tray?.config || {}
);
const automationAuthHeader = computed(
  () => pluginConfig.value.AUTOMATION_AUTH_HEADER
);
const tokenFallbackHeader = computed(
  () => pluginConfig.value.WEBHOOK_TOKEN_HEADER
);
const tokenQueryParam = computed(
  () => pluginConfig.value.WEBHOOK_QUERY_PARAM || 'token'
);
const automationEventKeys = computed(
  () => pluginConfig.value.AUTOMATION_EVENT_KEYS || []
);
const automationEventLabels = computed(
  () => pluginConfig.value.AUTOMATION_EVENT_LABELS || {}
);
const payloadFields = computed(
  () => pluginConfig.value.AUTOMATION_PAYLOAD_FIELDS || []
);
const automationPayloadExample = computed(() =>
  JSON.stringify(
    {
      event_name: 'tray_order_created',
      scope_name: 'order',
      act: 'insert',
      seller_id: '123456',
      scope_id: '987654',
      customer: {
        email: 'jane.doe@example.com',
        phone: '+55 11 99999-0000',
      },
      order: {
        id: '987654',
        number: 'TRAY-987654',
        status: 'approved',
        payment_method: 'credit_card',
        total: '189.90',
        currency: 'BRL',
      },
      raw_payload: {
        source: 'tray-support-or-middleware',
      },
    },
    null,
    2
  )
);
const automationWebhookUrl = computed(
  () => `${window.location.origin}/tray/accounts/${accountId.value}/events`
);
const submitLabel = computed(() =>
  editingHookId.value
    ? t('INTEGRATION_SETTINGS.TRAY.UPDATE_CONNECTION')
    : t('INTEGRATION_SETTINGS.TRAY.SAVE_CONNECTION')
);

const buildAutomationHeaderValue = token =>
  `${automationAuthHeader.value}: Bearer ${token || '...'}`;

const buildFallbackHeaderValue = token =>
  `${tokenFallbackHeader.value}: ${token || '...'}`;

const buildQueryTokenValue = token =>
  `${tokenQueryParam.value}=${token || '...'}`;

const resetForm = () => {
  editingHookId.value = null;
  storeUrl.value = '';
  consumerKey.value = '';
  consumerSecret.value = '';
  formError.value = '';
};

const openCreateForm = () => {
  resetForm();
  viewMode.value = 'form';
};

const openEditForm = hook => {
  editingHookId.value = hook.id;
  storeUrl.value = hook.storeUrl || '';
  consumerKey.value = '';
  consumerSecret.value = '';
  formError.value = '';
  viewMode.value = 'form';
};

const openOverview = () => {
  resetForm();
  viewMode.value = 'overview';
};

const handleSave = async () => {
  try {
    formError.value = '';
    isSubmitting.value = true;
    const { data } = await authorizeTray(accountId.value, {
      hook_id: editingHookId.value,
      store_url: storeUrl.value,
      consumer_key: consumerKey.value,
      consumer_secret: consumerSecret.value,
    });

    if (data?.redirect_url) {
      window.location.assign(data.redirect_url);
      return;
    }

    await store.dispatch('integrations/get');
    openOverview();
  } catch (err) {
    formError.value =
      err?.response?.data?.error || t('INTEGRATION_SETTINGS.TRAY.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const handleCopy = async value => {
  if (!value) {
    return;
  }

  await copyTextToClipboard(value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};

const handleDeleteHook = async hookId => {
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: integrationId.value,
      hookId,
    });
    await store.dispatch('integrations/get');
    if (editingHookId.value === hookId) {
      openOverview();
    }
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE'));
  }
};

const initializeIntegration = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeIntegration();
});
</script>

<template>
  <div class="flex flex-1 flex-col gap-6 overflow-auto pt-1 pb-10">
    <template v-if="integrationLoaded">
      <section
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div
          class="flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between"
        >
          <div class="max-w-3xl">
            <h2 class="mb-2 text-xl font-medium text-n-slate-12">
              {{ integrationName }}
            </h2>
            <p class="m-0 text-sm leading-6 text-n-slate-11">
              {{ integrationDescription }}
            </p>
          </div>
          <div class="flex gap-3">
            <Button
              v-if="viewMode === 'form'"
              slate
              faded
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click="openOverview"
            />
            <Button
              teal
              :label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
              @click="openCreateForm"
            />
          </div>
        </div>
      </section>

      <section
        v-if="viewMode === 'overview' && connectedHooks.length"
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="grid grid-cols-1 gap-4 p-6 xl:grid-cols-2">
          <div
            v-for="hook in connectedHooks"
            :key="hook.id"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 break-all text-sm font-medium text-n-slate-12">
                  {{ hook.storeName || hook.storeHost || hook.storeUrl }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  <span>{{
                    $t('INTEGRATION_SETTINGS.TRAY.FIELDS.STORE_URL')
                  }}</span>
                  <span class="mt-1 block break-all">{{
                    hook.storeUrl || '—'
                  }}</span>
                </p>
                <p class="mb-0 mt-2 text-xs text-n-slate-10">
                  <span>{{
                    $t('INTEGRATION_SETTINGS.TRAY.FIELDS.API_ADDRESS')
                  }}</span>
                  <span class="mt-1 block break-all">{{
                    hook.apiAddress || '—'
                  }}</span>
                </p>
                <p class="mb-0 mt-2 text-xs text-n-slate-10">
                  <span>{{
                    $t('INTEGRATION_SETTINGS.TRAY.FIELDS.STORE_ID')
                  }}</span>
                  <span class="mt-1 block break-all">{{
                    hook.storeId || '—'
                  }}</span>
                </p>
              </div>
              <div class="flex gap-2">
                <Button
                  slate
                  xs
                  faded
                  :label="$t('INTEGRATION_SETTINGS.WEBHOOK.CONFIGURE')"
                  @click="openEditForm(hook)"
                />
                <Button
                  ruby
                  xs
                  faded
                  :label="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')"
                  @click="handleDeleteHook(hook.id)"
                />
              </div>
            </div>

            <div class="mt-4 space-y-4">
              <div>
                <div class="flex items-start justify-between gap-4">
                  <p class="mb-0 text-sm font-medium text-n-slate-12">
                    {{ $t('INTEGRATION_SETTINGS.TRAY.WEBHOOK_URL') }}
                  </p>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="handleCopy(automationWebhookUrl)"
                  />
                </div>
                <code
                  class="mt-3 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
                >
                  {{ automationWebhookUrl }}
                </code>
              </div>

              <div>
                <div class="flex items-start justify-between gap-4">
                  <p class="mb-0 text-sm font-medium text-n-slate-12">
                    {{ $t('INTEGRATION_SETTINGS.TRAY.AUTH_HEADER') }}
                  </p>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="
                      handleCopy(buildAutomationHeaderValue(hook.webhookToken))
                    "
                  />
                </div>
                <code
                  class="mt-3 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
                >
                  {{ buildAutomationHeaderValue(hook.webhookToken) }}
                </code>
                <p class="mb-0 mt-2 break-all text-xs text-n-slate-10">
                  {{ buildFallbackHeaderValue(hook.webhookToken) }}
                </p>
                <p class="mb-0 mt-2 break-all text-xs text-n-slate-10">
                  {{ buildQueryTokenValue(hook.webhookToken) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="viewMode === 'overview' && !connectedHooks.length"
        class="rounded-xl bg-n-alpha-3 p-6 shadow outline outline-1 outline-n-container"
      >
        <p class="m-0 text-sm leading-6 text-n-slate-11">
          {{ $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', { integrationId }) }}
        </p>
      </section>

      <section
        v-if="viewMode === 'form'"
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="border-b border-n-weak p-6">
          <h3 class="text-xl font-medium text-n-slate-12">
            {{
              selectedHook
                ? $t('INTEGRATION_SETTINGS.WEBHOOK.CONFIGURE')
                : $t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')
            }}
          </h3>
          <p class="mb-0 mt-2 max-w-4xl text-sm leading-6 text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.TRAY.CONNECTION_HELP') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 p-6 lg:grid-cols-2">
          <Input
            v-model="storeUrl"
            type="url"
            :label="$t('INTEGRATION_SETTINGS.TRAY.FIELDS.STORE_URL')"
          />
          <Input
            v-model="consumerKey"
            :label="$t('INTEGRATION_SETTINGS.TRAY.FIELDS.CONSUMER_KEY')"
          />
          <Input
            v-model="consumerSecret"
            type="password"
            autocomplete="new-password"
            :label="$t('INTEGRATION_SETTINGS.TRAY.FIELDS.CONSUMER_SECRET')"
          />
        </div>

        <div class="border-t border-n-weak p-6">
          <div class="flex gap-3">
            <Button
              slate
              faded
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click="openOverview"
            />
            <Button
              teal
              :label="submitLabel"
              :is-loading="isSubmitting"
              @click="handleSave"
            />
          </div>
        </div>
        <p v-if="formError" class="m-0 px-6 pb-6 text-sm text-n-ruby-9">
          {{ formError }}
        </p>
      </section>

      <div
        v-if="error"
        class="flex flex-1 items-center justify-center rounded-md bg-n-alpha-3 p-6 shadow outline outline-1 outline-n-container"
      >
        <p class="text-n-ruby-9">
          {{ $t('INTEGRATION_SETTINGS.TRAY.ERROR') }}
        </p>
      </div>

      <section
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="border-b border-n-weak p-6">
          <h3 class="text-xl font-medium text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.TRAY.AUTOMATION_TITLE') }}
          </h3>
          <p class="mb-0 mt-2 max-w-4xl text-sm leading-6 text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.TRAY.AUTOMATION_DESCRIPTION') }}
          </p>
          <p class="mb-0 mt-3 max-w-4xl text-sm leading-6 text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.TRAY.WEBHOOK_HELP') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 p-6 lg:grid-cols-3">
          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.TRAY.WEBHOOK_URL') }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{
                    $t('INTEGRATION_SETTINGS.TRAY.STORE_URL_HINT', {
                      storeUrl: selectedHook?.storeUrl || storeUrl || '—',
                    })
                  }}
                </p>
              </div>
              <Button
                slate
                xs
                faded
                :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                @click="handleCopy(automationWebhookUrl)"
              />
            </div>
            <code
              class="mt-3 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
            >
              {{ automationWebhookUrl }}
            </code>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TRAY.EXPECTED_EVENTS') }}
            </p>
            <ul
              class="mb-0 mt-3 list-disc space-y-2 pl-4 text-sm text-n-slate-11"
            >
              <li v-for="eventKey in automationEventKeys" :key="eventKey">
                {{ automationEventLabels[eventKey] || eventKey }}
              </li>
            </ul>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.TRAY.EXPECTED_PAYLOAD') }}
            </p>
            <ul
              class="mb-0 mt-3 list-disc space-y-2 pl-4 text-sm text-n-slate-11"
            >
              <li v-for="field in payloadFields" :key="field">
                {{ field }}
              </li>
            </ul>
            <p class="mb-0 mt-4 text-xs leading-5 text-n-slate-10">
              {{ buildAutomationHeaderValue(selectedHook?.webhookToken) }}
            </p>
            <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
              {{ buildFallbackHeaderValue(selectedHook?.webhookToken) }}
            </p>
            <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
              {{ buildQueryTokenValue(selectedHook?.webhookToken) }}
            </p>
          </div>
        </div>

        <div class="px-6 pb-6">
          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.TRAY.PAYLOAD_EXAMPLE_TITLE') }}
                </p>
                <p class="mb-0 text-xs leading-5 text-n-slate-10">
                  {{
                    $t('INTEGRATION_SETTINGS.TRAY.PAYLOAD_EXAMPLE_DESCRIPTION')
                  }}
                </p>
              </div>
              <Button
                slate
                xs
                faded
                :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                @click="handleCopy(automationPayloadExample)"
              />
            </div>
            <pre
              class="mt-4 overflow-x-auto rounded-md bg-n-alpha-2 px-3 py-3 text-xs leading-6 text-n-slate-11"
            ><code>{{ automationPayloadExample }}</code></pre>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
